"use client";

import { useState, useMemo } from "react";
import { Button, Select as RACSelect, SelectValue } from "react-aria-components";
import { useLocale, useTranslations } from "next-intl";
import { clsx } from "clsx";
import type { Locale } from "@mw/shared/i18n";
import { getPriceForCurrency } from "@mw/shared/pricing";
import { EventName } from "@mw/shared/analyticsEventNames";
import { useCurrency } from "@/app/_providers/currency";
import { formatPrice } from "@utilities/numbers";
import Image from "next/image";
import { m, AnimatePresence } from "motion/react";

import IconChevronDownSmall from "@ui/central-icons/icons/IconChevronDownSmall";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { ListBox } from "../../core/ListBox";
import { Noise } from "../../core/Noise/Noise";
import { LinkTracked } from "../../core/LinkTracked";
import { TIER_COVER_IMAGES } from "../utils";
import { type PaygDisplayUnit, type PaygProductId, getPaygProductOptions } from "../payg-pricing-data";
import { formatMinutes, useMinutesSlider } from "./calculatorUtils";
import { usePricingTableContext } from "../PricingTableContext";
import { MinutesSlider } from "./MinutesSlider";
import { StepIndicator } from "./StepIndicator";
import { appUrls } from "@/constants";

const MAX_PAYG_MINUTES = 10_000;
const SIGN_UP_URL = appUrls.developers;

// ---------------------------------------------------------------------------
// Cost result card
// ---------------------------------------------------------------------------

interface CostResultCardProps {
  estimatedCost: string;
  productLabel: string;
  formattedMinutes: string;
  priceLabel: string;
  priceUnit: PaygDisplayUnit;
}

function CostResultCard({ estimatedCost, productLabel, formattedMinutes, priceLabel, priceUnit }: CostResultCardProps) {
  const t = useTranslations("PricingClientKeys.PaygCostCalculator");
  return (
    <div className='tw-relative lg:tw-aspect-square tw-w-full'>
      <div className='tw-relative lg:tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-2xl'>
        <div className='tw-absolute tw-inset-0 tw-w-full tw-h-full tw-bg-cream' />

        <div className='tw-absolute tw-inset-0 tw-w-full tw-h-full tw-opacity-100'>
          <Image src={TIER_COVER_IMAGES.payg} alt='' fill className='tw-object-cover' quality={90} placeholder='blur' />
        </div>

        <Noise className='tw-rounded-2xl' />

        <div className='tw-relative tw-p-5 lg:tw-p-10 tw-flex tw-flex-col tw-h-full tw-z-10'>
          {/* Header */}
          <div className='tw-mb-4 lg:tw-mb-6'>
            <p className='tw-text-white/60 tw-type-xs tw-mb-2'>{t("estimated_cost")}</p>
            <h3 className='tw-text-white tw-type-5xl'>{estimatedCost}</h3>
          </div>

          {/* Details */}
          <div className='tw-space-y-2 lg:tw-space-y-3 tw-mb-6 lg:tw-mb-0'>
            <p className='tw-text-white/80 tw-type-base'>
              {t("for_minutes_of_product", { minutes: formattedMinutes, product: productLabel })}
            </p>
            <p className='tw-text-white/50 tw-type-xs'>
              {priceUnit === "hour"
                ? t("at_price_per_hour", { price: priceLabel })
                : t("at_price_per_minute", { price: priceLabel })}
            </p>
          </div>

          {/* CTA */}
          <div className='tw-mt-auto lg:-tw-mx-5 lg:-tw-mb-5'>
            <LinkTracked
              eventName={EventName.mw_pricing_card_cta_clicked}
              eventProperties={{ tier: "payg", source: "payg_cost_estimator" }}
              asButton
              buttonVariants={{ variant: "elevated", size: "lg", expand: true }}
              className='!tw-rounded-2xl'
              href={SIGN_UP_URL}
              label={t("cta_label")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface PaygCostCalculatorProps {
  /** Initial minutes when mounting. Converted to slider position internally. */
  initialMinutes?: number;
  /** Initial product selection. Defaults to "tts_flash". */
  initialProductId?: PaygProductId;
  /** Called with actual minutes when slider changes, for external state sync. */
  onMinutesChange?: (minutes: number) => void;
  /** Called when product selection changes, for external state sync. */
  onProductChanged?: (id: PaygProductId) => void;
}

export function PaygCostCalculator({
  initialMinutes,
  initialProductId,
  onMinutesChange,
  onProductChanged,
}: PaygCostCalculatorProps) {
  const t = useTranslations("PricingClientKeys.PaygCostCalculator");
  const locale = useLocale() as Locale;
  const { currency, currencySymbol } = useCurrency();
  const { featureFlags } = usePricingTableContext();

  const [selectedProductId, setSelectedProductId] = useState<PaygProductId>(initialProductId ?? "tts_flash");
  const { sliderValue, minutes, handleSliderChange } = useMinutesSlider(
    initialMinutes ?? 50,
    MAX_PAYG_MINUTES,
    onMinutesChange,
  );

  const paygProductOptions = useMemo(() => getPaygProductOptions({ featureFlags }), [featureFlags]);

  const selectedProduct = useMemo(
    () => paygProductOptions.find(p => p.id === selectedProductId) ?? paygProductOptions[0],
    [paygProductOptions, selectedProductId],
  );

  const productItems = useMemo(
    () =>
      paygProductOptions.map(p => ({
        id: p.id,
        textValue: t(`products.${p.id}` as Parameters<typeof t>[0]),
      })),
    [paygProductOptions, t],
  );

  const estimatedCostUsd = minutes * selectedProduct.pricePerMinute;
  const formattedCost = formatPrice(getPriceForCurrency(estimatedCostUsd, currency), currencySymbol, locale);
  const productLabel = t(`products.${selectedProductId}` as Parameters<typeof t>[0]);
  const formattedMinutes = formatMinutes(minutes, "min");
  const priceUnit = selectedProduct.displayUnit;
  const displayedPriceConverted = getPriceForCurrency(selectedProduct.displayUnitPrice, currency);
  const formattedPrice = `${currencySymbol}${displayedPriceConverted < 0.01 ? displayedPriceConverted.toFixed(4) : displayedPriceConverted.toFixed(2)}`;

  return (
    <div className='tw-flex tw-flex-col lg:tw-flex-row tw-gap-8 lg:tw-gap-16'>
      {/* Left side - Product selector and minutes slider */}
      <div className='tw-w-full lg:tw-w-1/2 tw-space-y-8'>
        {/* Step 1: Product selector */}
        <div>
          <StepIndicator step={1} label={t("select_product")} />

          <RACSelect
            selectedKey={selectedProductId}
            onSelectionChange={key => {
              if (key !== null && typeof key === "string") {
                const id = key as PaygProductId;
                setSelectedProductId(id);
                onProductChanged?.(id);
              }
            }}
            aria-label={t("select_product")}
          >
            <Button
              className={({ isFocusVisible }) =>
                clsx(
                  "tw-w-full tw-text-base tw-font-normal tw-text-black tw-flex tw-items-center tw-gap-2 tw-h-11 tw-px-4 tw-rounded-xl tw-bg-gray-100 hover:tw-bg-gray-200 tw-transition-colors tw-cursor-pointer",
                  isFocusVisible ? "tw-outline-focus" : "tw-outline-none",
                )
              }
            >
              <SelectValue className='tw-flex-1 tw-text-left' />
              <IconChevronDownSmall className={`tw-size-4 tw-text-gray-400 tw-flex-none ${iconStrokeFix}`} />
            </Button>
            <ListBox items={productItems} offset={8} />
          </RACSelect>
        </div>

        {/* Step 2: Minutes slider */}
        <div>
          <StepIndicator step={2} label={t("minutes_per_month")} />

          <MinutesSlider
            sliderValue={sliderValue}
            onSliderChange={handleSliderChange}
            maxMinutes={MAX_PAYG_MINUTES}
            ariaLabel={t("minutes_per_month")}
          />
        </div>
      </div>

      {/* Right side - Cost result card */}
      <div className='tw-w-full lg:tw-w-1/2'>
        <AnimatePresence mode='popLayout' initial={false}>
          <m.div
            key={selectedProductId}
            layout
            layoutId='payg-cost-card'
            initial={{ opacity: 0.7 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0.7 }}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            <CostResultCard
              estimatedCost={formattedCost}
              productLabel={productLabel}
              formattedMinutes={formattedMinutes}
              priceLabel={formattedPrice}
              priceUnit={priceUnit}
            />
          </m.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
